import clsx from 'clsx'
import { ReactNode } from 'react'

import BvaRingArt from 'assets/images/bvaDetails/bva-ring.png'
import { Section } from 'model/section'

import { buildBvaPdfContent } from './bvaPdfContent'
import { BVA_PDF_ROOT_ID } from './bvaPdfLayout'
import { BvaPdfWedge } from './BvaPdfWedge'
import './BvaPdfDocument.scss'

export interface BvaPdfDocumentProps {
  brandName?: string
  year: number
  logoUrl: string
  backgroundUrl?: string
  sections: Section[]
}

/*
  Figma renders these two titles on two lines (nodes 5228:34077 / 5228:34081).
  Explicit breaks rather than relying on the 348.32px box to wrap naturally,
  because the natural break point sits close enough to the box width that a
  font-metric change could flip it. Keyed by questionText so the mapping can
  never drift from bvaPdfContent.ts's question list.
*/
const TITLE_OVERRIDES: Record<string, ReactNode> = {
  'What are their belief/values?': (
    <>
      What are their
      <br />
      belief/values?
    </>
  ),
  'What need are they trying to fulfill?': (
    <>
      What need are they
      <br />
      trying to fulfill?
    </>
  ),
}

interface CellProps {
  title: string
  body: string
  fitGroup: string
  fitMin: number
  fixedTitle?: boolean
}

const Cell = ({ title, body, fitGroup, fitMin, fixedTitle }: CellProps) => (
  <div className="bva-pdf__cell">
    <div className={clsx('bva-pdf__cell-title', fixedTitle && 'bva-pdf__cell-title--fixed')}>
      {TITLE_OVERRIDES[title] ?? title}
    </div>
    <div className="bva-pdf__cell-rule" />
    <p className="bva-pdf__cell-body bva-pdf__fit" data-fit-group={fitGroup} data-fit-min={fitMin}>
      {body}
    </p>
  </div>
)

interface PillarProps {
  title: string
  tagline: string
  fitGroup: string
}

const Pillar = ({ title, tagline, fitGroup }: PillarProps) => (
  <div className="bva-pdf__pillar" data-fit-probe="">
    <p className="bva-pdf__pillar-title">{title}</p>
    <p className="bva-pdf__pillar-tagline bva-pdf__fit" data-fit-group={fitGroup} data-fit-min={20}>
      {tagline}
    </p>
  </div>
)

/**
 * Offscreen 1920x1080 BVA one-pager, matching Figma frame 5228:34051.
 * Purely presentational: the export pipeline (exportBvaPdf.tsx) is
 * responsible for resolving `logoUrl`/`backgroundUrl` to data URIs, mounting
 * this outside #root, waiting for fonts/images, running the auto-fit pass,
 * and rasterizing it.
 */
export const BvaPdfDocument = ({ brandName, year, logoUrl, backgroundUrl, sections }: BvaPdfDocumentProps) => {
  const content = buildBvaPdfContent(sections)
  const headline = [brandName, year].filter(Boolean).join(' ')

  return (
    <div className="bva-pdf" id={BVA_PDF_ROOT_ID}>
      {/* 5228:34052  header band, 0,0 1920x362 */}
      <div className="bva-pdf__header">
        <div className="bva-pdf__header-base" />
        {backgroundUrl ? <img className="bva-pdf__header-image" src={backgroundUrl} alt="" /> : null}
        <div className="bva-pdf__header-scrim" />

        <div className="bva-pdf__header-row">
          {/* 5228:34055 */}
          <div className="bva-pdf__logo">
            <img className="bva-pdf__logo-image" src={logoUrl} alt="" />
          </div>
          {/* 5228:34056 -- single nowrap line: scope, probe and fit element are
              all this one node, measured by width rather than height. */}
          <p
            className="bva-pdf__title bva-pdf__fit"
            data-fit-scope="header-title"
            data-fit-axis="width"
            data-fit-probe=""
            data-fit-group="header-title"
            data-fit-min={24}
          >
            {headline}
          </p>
        </div>
      </div>

      {/* 5228:34057  card, 40,152 1840x888 */}
      <div className="bva-pdf__card">
        {/* 5228:34058 / 5228:34059 */}
        <div className="bva-pdf__card-half bva-pdf__card-half--left" />
        <div className="bva-pdf__card-half bva-pdf__card-half--right" />

        {/* 5228:34060 */}
        <div className="bva-pdf__wedge">
          <BvaPdfWedge />
        </div>

        {/* 5228:34061  centre ring */}
        <div className="bva-pdf__ring">
          {/* 5228:34063 -- angular gradient, exported raster */}
          <img className="bva-pdf__ring-art" src={BvaRingArt} alt="" />
          {/* 5228:34064 */}
          <div className="bva-pdf__ring-inner" data-fit-scope="mbr">
            {/* 5228:34065 */}
            <div className="bva-pdf__ring-text" data-fit-probe="">
              {/* 5228:34066 */}
              <p className="bva-pdf__ring-title">
                MEANINGFUL
                <br />
                BRAND ROLE
              </p>
              {/* 5228:34067 */}
              <p className="bva-pdf__ring-body bva-pdf__fit" data-fit-group="mbr" data-fit-min={12}>
                {content.meaningfulBrandRole}
              </p>
            </div>
          </div>
        </div>

        {/* 5228:34068  top row, 40,40 1760 wide */}
        <div className="bva-pdf__row bva-pdf__row--top">
          {/* 5228:34069 */}
          <div className="bva-pdf__col bva-pdf__col--top" data-fit-scope="tension-pillar">
            <Pillar title="HUMAN TENSION" tagline={content.tensionTagline} fitGroup="tension-tagline" />
          </div>

          {/* 5228:34072 / 34076 / 34080 */}
          {content.humanTension.map((cell, index) => (
            <div
              key={cell.question}
              className="bva-pdf__col bva-pdf__col--top"
              data-fit-scope={`tension-q${index + 1}`}
            >
              <div className="bva-pdf__cell-wrap" data-fit-probe="">
                <Cell title={cell.question} body={cell.answer} fitGroup="tension-bodies" fitMin={11} fixedTitle />
              </div>
            </div>
          ))}
        </div>

        {/* 5228:34084  bottom row, 40,329 1760 wide */}
        <div className="bva-pdf__row bva-pdf__row--bottom">
          {/* 5228:34085 */}
          <div className="bva-pdf__col bva-pdf__col--bottom bva-pdf__col--product" data-fit-scope="product-stack">
            <div className="bva-pdf__stack" data-fit-probe="">
              {content.productEdge.map(cell => (
                <Cell
                  key={cell.question}
                  title={cell.question}
                  body={cell.answer}
                  fitGroup="product-bodies"
                  fitMin={11}
                />
              ))}
            </div>
          </div>

          {/* 5228:34102 */}
          <div className="bva-pdf__col bva-pdf__col--bottom bva-pdf__col--product" data-fit-scope="product-pillar">
            <Pillar title="PRODUCT EDGE" tagline={content.productEdgeTagline} fitGroup="product-tagline" />
          </div>

          {/* 5228:34105 */}
          <div className="bva-pdf__col bva-pdf__col--bottom bva-pdf__col--brand" data-fit-scope="brand-pillar">
            <Pillar title="BRAND EDGE" tagline={content.brandEdgeTagline} fitGroup="brand-tagline" />
          </div>

          {/* 5228:34108 */}
          <div className="bva-pdf__col bva-pdf__col--bottom bva-pdf__col--brand" data-fit-scope="brand-stack">
            <div className="bva-pdf__stack" data-fit-probe="">
              {content.brandEdge.map(cell => (
                <Cell
                  key={cell.question}
                  title={cell.question}
                  body={cell.answer}
                  fitGroup="brand-bodies"
                  fitMin={11}
                />
              ))}
            </div>
          </div>
        </div>

        {/* 1.329px stroke, drawn last so it sits over everything else */}
        <div className="bva-pdf__card-border" />
      </div>
    </div>
  )
}
